import './HealthSummary.scss';
import {
  Accordion,
  AccordionItem,
  CollapseCard,
  Grid,
  GridItem,
  Heading,
  PendingContent,
  Space,
  Spinner,
} from 'plume-ui';
import { HeadingStyles } from 'plume-ui/dist/components/Heading/Heading';
import React, { memo, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { MixPanelEvents } from '../../../mixPanelEvents';
import { locationCountAtom } from '../../../state';
import FormattedMessage from '../../../utils/components/FormattedMessage';
import { AdjustableComponent } from '../../../utils/hooks/useClasses';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';
import Download from '../components/Download/Download';
import HeroStatsRow from '../components/HeroStatsRow/HeroStatsRow';
import IssueMatrixTable from '../components/IssueMatrixTable/IssueMatrixTable';
import TimelineInteractions from '../components/TimelineInteractions/TimelineInteractions';
import Trending from '../components/Trending/Trending';
import { styles } from './fixtures';
import { useTranslation } from 'react-i18next';
import CSPOutageMap from '../components/CSPOutageMap/CSPOutageMap';
import WarningAndAlertTable from '../components/WarningAndAlertTable/WarningAndAlertTable';
import useAutonomousMonitors from '../../Monitor/hooks/useAutonomousMonitors';

type HealthSummaryProps = {};

export type HealthSummaryGenericStyles = {
  root: string;
  title: string;
  menuRow: string;
  summaryCell: string;
};

const HealthSummary: AdjustableComponent<
  HealthSummaryProps,
  HealthSummaryGenericStyles
> = () => {
  const { t } = useTranslation();

  const trackEvent = useTrackEvent();

  const { data, isLoading } = useAutonomousMonitors();

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.HealthSummary,
      },
    });
  }, []);

  const hasAutoMonitors = !isLoading && (data?.length ?? 0) > 0;

  return (
    <div className="HealthSummary">
      <Grid>
        <GridItem colSpan="12" tabletColSpan="12">
          <Space size="l" />
          <div className="HealthSummary__header-container">
            <Heading
              classes={(current: HeadingStyles) => ({
                root: `${current.root} AppLayout__header HealthSummary__header`,
              })}
              size="xxxxl"
              medium
            >
              <FormattedMessage id="healthSummary.title" />
            </Heading>
            <div>
              <Download />
            </div>
          </div>
        </GridItem>

        <GridItem colSpan="12" tabletColSpan="12">
          <HeroStatsRow />
        </GridItem>

        {isLoading && (
          <GridItem colSpan="12" tabletColSpan="12">
            <div className="HealthSummary__loading">
              <Spinner size="small" visible />
            </div>
          </GridItem>
        )}

        {hasAutoMonitors && (
          <>
            <GridItem colSpan="12" tabletColSpan="12">
              <CSPOutageMap />
            </GridItem>
            <Space size="m" />
          </>
        )}

        {hasAutoMonitors && (
          <>
            <GridItem colSpan="12" tabletColSpan="12">
              <WarningAndAlertTable />
            </GridItem>
            <Space size="m" />
          </>
        )}

        {!isLoading && (
          <>
            <GridItem colSpan="12" tabletColSpan="12">
              <CollapseCard
                open={!hasAutoMonitors}
                onStateChange={(open) => {
                  if (open)
                    trackEvent({
                      eventName:
                        MixPanelEvents.HEALTH_SUMMARY_EXPAND_ISP_SCORES,
                    });
                }}
                title={t('healthSummary.chartExpandableTitle')}
              >
                <Trending passedClasses={styles} />
                <Space size="xl" />
                <TimelineInteractions passedClasses={styles} />
              </CollapseCard>
            </GridItem>

            <Space size="s" />

            <GridItem colSpan="12" tabletColSpan="12">
              <CollapseCard
                open={!hasAutoMonitors}
                onStateChange={(open) => {
                  if (open)
                    trackEvent({
                      eventName:
                        MixPanelEvents.HEALTH_SUMMARY_EDXPAND_ISSUE_MATRIX,
                    });
                }}
                title={t('healthSummary.issueMatrixTable.title')}
              >
                <IssueMatrixTable passedClasses={styles} />
              </CollapseCard>
            </GridItem>

            <Space size="xl" />
          </>
        )}
      </Grid>
    </div>
  );
};

export default memo(HealthSummary);
